function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function markdownToHtml(text) {
  if (!text) return "";
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  return html.replace(/\n/g, "<br>");
}

function walkNodes(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (node.nodeName === "BR") return "\n";
  if (node.nodeName === "DIV" || node.nodeName === "P") {
    const inner = Array.from(node.childNodes).map(walkNodes).join("");
    return `${inner}\n`;
  }
  if (node.nodeName === "STRONG" || node.nodeName === "B") {
    return `**${Array.from(node.childNodes).map(walkNodes).join("")}**`;
  }
  if (node.nodeName === "EM" || node.nodeName === "I") {
    return `*${Array.from(node.childNodes).map(walkNodes).join("")}*`;
  }
  return Array.from(node.childNodes).map(walkNodes).join("");
}

export function htmlToMarkdown(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return walkNodes(div).replace(/\u00a0/g, " ").replace(/\n+$/g, "");
}

export function getSelectionOffsets(el) {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !el) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.commonAncestorContainer)) return null;
  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.startContainer, range.startOffset);
  const start = pre.toString().length;
  return { start, end: start + range.toString().length };
}

function markdownLengthThroughPoint(el, container, offset) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.setEnd(container, offset);
  const wrapper = document.createElement("div");
  wrapper.appendChild(range.cloneContents());
  return htmlToMarkdown(wrapper.innerHTML).length;
}

/** Selection offsets in markdown space (includes ** / * markers). */
export function getMarkdownSelectionOffsets(el) {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !el) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.commonAncestorContainer)) return null;
  const start = markdownLengthThroughPoint(el, range.startContainer, range.startOffset);
  const end = markdownLengthThroughPoint(el, range.endContainer, range.endOffset);
  return { start, end };
}

export function getEditorSelectionOffsets(el, { richText = false } = {}) {
  return richText ? getMarkdownSelectionOffsets(el) : getSelectionOffsets(el);
}

export function setSelectionOffset(el, offset) {
  if (!el) return;
  const sel = window.getSelection();
  const range = document.createRange();
  let remaining = Math.max(0, offset ?? 0);
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      range.setStart(node, remaining);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    remaining -= len;
    node = walker.nextNode();
  }
  range.selectNodeContents(el);
  range.collapse(false);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/** Place the caret at a markdown character offset inside a rich-text block. */
export function setMarkdownSelectionOffset(el, offset) {
  if (!el) return;
  const target = Math.max(0, offset ?? 0);
  const fullLen = htmlToMarkdown(el.innerHTML ?? "").length;
  if (target >= fullLen) {
    setSelectionOffset(el, Number.MAX_SAFE_INTEGER);
    return;
  }

  const sel = window.getSelection();
  const range = document.createRange();
  const textWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let textNode = textWalker.nextNode();

  if (!textNode) {
    range.selectNodeContents(el);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
    return;
  }

  while (textNode) {
    const len = textNode.textContent?.length ?? 0;
    for (let i = 0; i <= len; i += 1) {
      range.setStart(textNode, i);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      const pos = getMarkdownSelectionOffsets(el)?.start ?? 0;
      if (pos >= target) return;
    }
    textNode = textWalker.nextNode();
  }

  setSelectionOffset(el, Number.MAX_SAFE_INTEGER);
}

export function setEditorSelectionOffset(el, offset, { richText = false } = {}) {
  if (richText) setMarkdownSelectionOffset(el, offset);
  else setSelectionOffset(el, offset);
}

export function selectAllEditorContents(el) {
  if (!el) return;
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

export function isEntireEditorContentsSelected(el, plainText, { richText = false } = {}) {
  const offsets = getEditorSelectionOffsets(el, { richText });
  const len = (plainText ?? "").length;
  if (!offsets || len === 0) return false;
  return offsets.start === 0 && offsets.end >= len;
}

function countMarkerToggles(text, marker) {
  let toggles = 0;
  for (let i = 0; i <= text.length - marker.length; i += 1) {
    if (text.slice(i, i + marker.length) === marker) {
      toggles += 1;
      i += marker.length - 1;
    }
  }
  return toggles;
}

function countItalicToggles(text) {
  let toggles = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text.slice(i, i + 2) === "**") {
      i += 1;
      continue;
    }
    if (text[i] === "*") toggles += 1;
  }
  return toggles;
}

/** Split markdown at an offset without breaking open ** / * markers. */
export function splitMarkdownAt(text, offset) {
  const source = text || "";
  const len = source.length;
  const at = Math.max(0, Math.min(offset ?? len, len));
  let before = source.slice(0, at);
  let after = source.slice(at);

  if (countMarkerToggles(before, "**") % 2 === 1) {
    before += "**";
    after = `**${after}`;
  }
  if (countItalicToggles(before) % 2 === 1) {
    before += "*";
    after = `*${after}`;
  }

  return { before, after };
}

function caretRectForElement(el) {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !el) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.commonAncestorContainer)) return null;
  const rects = range.getClientRects();
  return rects.length ? rects[0] : range.getBoundingClientRect();
}

export function isCaretOnFirstLine(el) {
  if (!el) return true;
  const caretRect = caretRectForElement(el);
  if (!caretRect) return true;
  const elRect = el.getBoundingClientRect();
  if (!el.textContent?.length) return true;
  return Math.abs(caretRect.top - elRect.top) <= 6;
}

export function isCaretOnLastLine(el) {
  if (!el) return true;
  const caretRect = caretRectForElement(el);
  if (!caretRect) return true;
  const elRect = el.getBoundingClientRect();
  if (!el.textContent?.length) return true;
  return Math.abs(elRect.bottom - caretRect.bottom) <= 6;
}

export function wrapMarkdownSelection(markdown, start, end, wrapper) {
  const text = markdown || "";
  const len = text.length;
  const s = Math.max(0, Math.min(start, len));
  const e = Math.max(s, Math.min(end, len));
  if (s === e) {
    return { text: `${text.slice(0, s)}${wrapper}${wrapper}${text.slice(s)}`, cursor: s + wrapper.length };
  }
  return {
    text: `${text.slice(0, s)}${wrapper}${text.slice(s, e)}${wrapper}${text.slice(e)}`,
    cursor: e + wrapper.length * 2,
  };
}

export function applyInlineCommand(el, markdown, command) {
  const offsets = getMarkdownSelectionOffsets(el);
  if (!offsets) return null;
  const wrapper = command === "bold" ? "**" : "*";
  const wrapped = wrapMarkdownSelection(markdown, offsets.start, offsets.end, wrapper);
  return wrapped;
}
